/**
 * Unified onboarding state management.
 *
 * All guide/onboarding state lives in a single localStorage object per user:
 *   mnevia_onboarding_{userId}
 *
 * This replaces the scattered keys:
 *   - mnevia_onboarding_done_{userId}
 *   - mnevia_ideas_guide_dismissed
 *   - mnevia_stage_hints_dismissed
 *   - mnevia_iteration_guide_dismissed
 *   - mnevia_whats_new_seen
 *   - qs_progress_{userId}
 */

const STORAGE_PREFIX = 'mnevia_onboarding_';

const DEFAULT_STATE = {
  role_selected: false,
  profile_seeded: false,
  proposals_uploaded: false,
  onboarding_complete: false,
  ideas_guide_seen: false,
  stage_hints_seen: false,
  iteration_guide_seen: false,
  whats_new_version: null,
};

// ── Old key migration ───────────────────────────────────────────────────────

function _migrateOldKeys(userId) {
  const key = STORAGE_PREFIX + userId;
  // Only migrate if new key doesn't exist yet
  if (localStorage.getItem(key)) return;

  const state = { ...DEFAULT_STATE };

  // Migrate per-user keys
  if (localStorage.getItem(`mnevia_onboarding_done_${userId}`) === '1') {
    state.onboarding_complete = true;
    state.profile_seeded = true;
    state.role_selected = true;
  }

  // Migrate per-device keys (apply to this user)
  if (localStorage.getItem('mnevia_ideas_guide_dismissed') === '1') {
    state.ideas_guide_seen = true;
  }
  if (localStorage.getItem('mnevia_stage_hints_dismissed') === '1') {
    state.stage_hints_seen = true;
  }
  if (localStorage.getItem('mnevia_iteration_guide_dismissed') === '1') {
    state.iteration_guide_seen = true;
  }

  const whatsNew = localStorage.getItem('mnevia_whats_new_seen');
  if (whatsNew) {
    state.whats_new_version = whatsNew;
  }

  // Save migrated state
  localStorage.setItem(key, JSON.stringify(state));

  // Clean up old keys
  localStorage.removeItem(`mnevia_onboarding_done_${userId}`);
  localStorage.removeItem('mnevia_ideas_guide_dismissed');
  localStorage.removeItem('mnevia_stage_hints_dismissed');
  localStorage.removeItem('mnevia_iteration_guide_dismissed');
  // Keep mnevia_whats_new_seen for now (WhatsNewModal may still read it)
}

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Get the full onboarding state for a user. Migrates old keys on first access.
 */
export function getOnboardingState(userId) {
  if (!userId) return { ...DEFAULT_STATE };
  _migrateOldKeys(userId);

  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + userId);
    if (raw) return { ...DEFAULT_STATE, ...JSON.parse(raw) };
  } catch {}
  return { ...DEFAULT_STATE };
}

/**
 * Update specific fields in the onboarding state.
 * @param {number|string} userId
 * @param {object} updates — partial state to merge
 */
export function updateOnboarding(userId, updates) {
  if (!userId) return;
  const state = getOnboardingState(userId);
  const merged = { ...state, ...updates };
  localStorage.setItem(STORAGE_PREFIX + userId, JSON.stringify(merged));
}

/**
 * Check if a specific guide has been seen/dismissed.
 * @param {number|string} userId
 * @param {'ideas_guide'|'stage_hints'|'iteration_guide'} guideKey
 */
export function isGuideSeen(userId, guideKey) {
  const state = getOnboardingState(userId);
  const keyMap = {
    ideas_guide: 'ideas_guide_seen',
    stage_hints: 'stage_hints_seen',
    iteration_guide: 'iteration_guide_seen',
  };
  return !!state[keyMap[guideKey] || guideKey];
}

/**
 * Mark a guide as seen/dismissed.
 * @param {number|string} userId
 * @param {'ideas_guide'|'stage_hints'|'iteration_guide'} guideKey
 */
export function markGuideSeen(userId, guideKey) {
  const keyMap = {
    ideas_guide: 'ideas_guide_seen',
    stage_hints: 'stage_hints_seen',
    iteration_guide: 'iteration_guide_seen',
  };
  updateOnboarding(userId, { [keyMap[guideKey] || guideKey]: true });
}

/**
 * Check if the full onboarding flow has been completed.
 */
export function isOnboardingComplete(userId) {
  return getOnboardingState(userId).onboarding_complete;
}

/**
 * Mark the full onboarding as complete.
 */
export function completeOnboarding(userId) {
  updateOnboarding(userId, { onboarding_complete: true, role_selected: true, profile_seeded: true });
}

/**
 * Check if this user arrived via a magic link (outreach campaign).
 * If so, they should skip the standard onboarding flow.
 */
export function isMagicLinkUser(user) {
  return !!(user && user.source_campaign);
}
